import {
  Badge,
  Divider,
  Flex,
  Grid,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useDashboardData } from "src/hooks";

function getBadgeDetails(props: any) {
  const colorProps = props.value1.props.find(
    (prop: any) => prop.name.toLowerCase() === "__color"
  );
  return {
    value: props.value1.label || props.value1.value,
    color: colorProps ? colorProps.value : "gray",
  };
}

function Field({ data }: { data: any }) {
  if (data) {
    if (data.type === "BADGE") {
      const badgeDetails = getBadgeDetails(data);
      return (
        <Badge radius="xs" color={badgeDetails.color} variant="light">
          {badgeDetails.value}
        </Badge>
      );
    } else {
      return <Text size="xs">{data.value1.value}</Text>;
    }
  } else {
    return <Text size="xs">-</Text>;
  }
}

function Dashboard({
  entityId,
  reconsetId,
  reconsetRunPreference,
  runId,
  runSeq,
  dashboard,
}: {
  entityId: string | null | undefined;
  reconsetId: string | null | undefined;
  reconsetRunPreference: string;
  runId: string;
  runSeq: string;
  dashboard: {
    id: string;
    name: string;
    displayColumns: Array<{
      name: string;
      type: string;
      label: string;
    }>;
  };
}) {
  const theme = useMantineTheme();

  const { data, isFetched } = useDashboardData(
    entityId,
    reconsetId,
    runId,
    runSeq,
    dashboard.id
  );
  if (reconsetRunPreference === "CONTINUOUS") {
    return (
      <Stack>
        <Flex>
          <Title
            mt="xs"
            order={5}
            c="white"
            py={0}
            px={theme.spacing.xxs}
            style={{
              backgroundColor: theme.colors.pink[5],
            }}
          >
            {dashboard.name}
          </Title>
        </Flex>

        <ScrollArea style={{ height: 250 }} offsetScrollbars scrollbarSize={16}>
          <Table
            verticalSpacing="sm"
            fs="xs"
            horizontalSpacing={"xs"}
            style={{ marginBottom: theme.spacing.lg }}
          >
            <Table.Thead>
              <Table.Tr>
                {dashboard.displayColumns.map((col) => {
                  return (
                    <Table.Th
                      key={col.name}
                      style={{
                        minWidth: `${col.label.length * 7}px`,
                      }}
                    >
                      {col.label}
                    </Table.Th>
                  );
                })}
              </Table.Tr>
            </Table.Thead>
            {isFetched ? (
              <>
                {data.previousDashboardData &&
                data.previousDashboardData?.length > 0 ? (
                  <Table.Tbody>
                    {(data.previousDashboardData || []).map(
                      (rowData: any, index: number) => {
                        return (
                          <Table.Tr key={index}>
                            {dashboard.displayColumns.map((col, index) => {
                              return (
                                <Table.Td key={col.name}>
                                  <Field data={rowData[`field_${index + 1}`]} />
                                </Table.Td>
                              );
                            })}
                          </Table.Tr>
                        );
                      }
                    )}
                  </Table.Tbody>
                ) : (
                  <>
                    <Divider
                      label="No Data"
                      color="gray"
                      mt="xs"
                      ml="xs"
                      labelPosition="left"
                    />
                  </>
                )}
              </>
            ) : (
              <Divider
                label="Fetching Data"
                color="gray"
                mt="xs"
                ml="xs"
                labelPosition="left"
              />
            )}
          </Table>
        </ScrollArea>
      </Stack>
    );
  } else {
    return (
      <Stack>
        <Flex>
          <Title
            mt="xs"
            order={5}
            c="white"
            py={0}
            px={theme.spacing.xxs}
            style={{
              backgroundColor: theme.colors.pink[5],
            }}
          >
            {dashboard.name}
          </Title>
        </Flex>

        <ScrollArea style={{ height: 250 }} offsetScrollbars scrollbarSize={16}>
          <Table
            verticalSpacing="sm"
            fs="xs"
            horizontalSpacing={"xs"}
            style={{ marginBottom: "20px" }}
          >
            <Table.Thead>
              <Table.Tr>
                {dashboard.displayColumns.map((col) => {
                  return (
                    <Table.Th
                      key={col.name}
                      style={{
                        minWidth: `${col.label.length * 7}px`,
                      }}
                    >
                      {col.label}
                    </Table.Th>
                  );
                })}
              </Table.Tr>
            </Table.Thead>
            {isFetched ? (
              <>
                {data.dashboardData && data.dashboardData?.length > 0 ? (
                  <Table.Tbody>
                    {(data.dashboardData || []).map(
                      (rowData: any, index: number) => {
                        return (
                          <Table.Tr key={index}>
                            {dashboard.displayColumns.map((col, index) => {
                              return (
                                <Table.Td key={col.name}>
                                  <Field data={rowData[`field_${index + 1}`]} />
                                </Table.Td>
                              );
                            })}
                          </Table.Tr>
                        );
                      }
                    )}
                  </Table.Tbody>
                ) : (
                  <>
                    <Divider
                      label="No Data"
                      color="gray"
                      labelPosition="left"
                    />
                  </>
                )}
              </>
            ) : (
              <Divider
                label="Fetching Data"
                color="gray"
                labelPosition="left"
              />
            )}
          </Table>
        </ScrollArea>
      </Stack>
    );
  }
}

export default function Dashboards({
  entityId,
  reconsetId,
  reconsetRunPreference,
  runId,
  runSeq,
  dashboards,
}: {
  entityId: string | null | undefined;
  reconsetId: string | null | undefined;
  reconsetRunPreference: string;
  runId: string;
  runSeq: string;
  dashboards: Array<{
    id: string;
    name: string;
    displayColumns: Array<{
      name: string;
      type: string;
      label: string;
    }>;
  }>;
}) {
  if (dashboards && dashboards.length > 0) {
    return (
      <>
        {dashboards.map((dashboard, index) => {
          return (
            <Dashboard
              key={dashboard.id}
              entityId={entityId}
              reconsetId={reconsetId}
              reconsetRunPreference={reconsetRunPreference}
              runId={runId}
              runSeq={runSeq}
              dashboard={dashboard}
            />
          );
        })}
      </>
    );
  } else {
    return <Divider label="No Dashboards" labelPosition="left" />;
  }
}
